// SPDX-License-Identifier: AGPL-3.0-or-later

// Popout shown when a role mention is clicked: the role's members, like Discord.

import styles from '@app/features/messaging/components/markdown/renderers/RoleMembersPopout.module.css';
import {UserServerTagBadge} from '@app/features/guild/components/server_tag/ServerTagBadge';
import Guilds from '@app/features/guild/state/Guilds';
import GuildMembers from '@app/features/member/state/GuildMembers';
import * as ColorUtils from '@app/features/theme/utils/ColorUtils';
import {OwnerCrownIcon} from '@app/features/ui/components/icons/OwnerCrownIcon';
import {StatusAwareAvatar} from '@app/features/ui/components/StatusAwareAvatar';
import * as UserProfileCommands from '@app/features/user/commands/UserProfileCommands';
import * as NicknameUtils from '@app/features/user/utils/NicknameUtils';
import {observer} from 'mobx-react-lite';

const MAX_SHOWN = 100;

interface RoleMembersPopoutProps {
	guildId: string;
	roleId: string;
	roleName: string;
	roleColor: number;
	onClose: () => void;
}

export const RoleMembersPopout = observer(function RoleMembersPopout({
	guildId,
	roleId,
	roleName,
	roleColor,
	onClose,
}: RoleMembersPopoutProps) {
	const members = GuildMembers.getMembers(guildId)
		.filter((member) => member.roles.has(roleId))
		.map((member) => ({member, name: NicknameUtils.getNickname(member.user, guildId)}))
		.sort((a, b) => a.name.localeCompare(b.name));
	const color = roleColor ? ColorUtils.int2rgb(roleColor) : undefined;
	const ownerId = Guilds.getGuild(guildId)?.ownerId;
	return (
		<div className={styles.root} data-flx="messaging.role-members-popout">
			<div className={styles.header}>
				<span className={styles.dot} style={color ? {background: color} : undefined} />
				<span className={styles.title}>{roleName}</span>
				<span className={styles.count}>— {members.length}</span>
			</div>
			<div className={styles.list}>
				{members.slice(0, MAX_SHOWN).map(({member, name}) => (
					<button
						key={member.user.id}
						type="button"
						className={styles.row}
						onClick={() => {
							onClose();
							UserProfileCommands.openUserProfile(member.user.id, guildId);
						}}
					>
						<StatusAwareAvatar user={member.user} size={32} guildId={guildId} />
						<span className={styles.name} style={color ? {color} : undefined}>
							{name}
						</span>
						<UserServerTagBadge user={member.user} size="sm" />
						{member.user.id === ownerId && <OwnerCrownIcon className={styles.crown} />}
					</button>
				))}
			</div>
		</div>
	);
});
